import { Controller, useFormContext } from 'react-hook-form'

import { Box, Button } from '@factorialco/deprecated-design-system'

import NumberField from 'modules/core/components/NumberField'
import i18n from 'modules/core/lib/i18n'

import { castError } from 'modules/expenses/components/expenses/Detail/_newForm/castError'
import InlineFields from 'modules/expenses/components/expenses/Form/InlineFields'
import { FieldTypes, Tax } from 'modules/expenses/hooks/useExpensesCustomization'

interface Props {
  tax: Tax
  taxFieldType: string
  index: number
  currency: string
  onChange: (tax: Tax) => void
  onDelete: (index: number) => void
  isFieldMandatory: boolean
  isFieldReadOnly: boolean
}

const TaxFields = ({
  tax,
  index,
  taxFieldType,
  currency,
  onDelete,
  isFieldMandatory,
  isFieldReadOnly,
}: Props) => {
  const { control, setValue } = useFormContext()
  const calculateAmount = (
    baseAmount: number | null | undefined,
    percentage: number | null | undefined
  ) => {
    if (typeof baseAmount !== 'number' || isNaN(baseAmount)) {
      return null
    }
    if (typeof percentage !== 'number' || isNaN(percentage)) {
      return null
    }

    return baseAmount * (percentage / 100)
  }

  if (taxFieldType === FieldTypes.cents) {
    return (
      <Controller
        name={`taxes.${0}.amount`}
        control={control}
        render={({ field, fieldState }) => (
          <NumberField
            value={field.value ?? ''}
            label={i18n.t('expenses_v2.forms.fields.vat_amount.label')}
            indicator={currency}
            mandatory={isFieldMandatory}
            error={castError(fieldState.error)}
            maxDecimals={2}
            placeholder="0"
            onChange={(value) => {
              field.onChange(value)
            }}
            readonly={isFieldReadOnly}
          />
        )}
      />
    )
  }

  const hideDelete = isFieldReadOnly || (isFieldMandatory && index === 0)

  return (
    <>
      <Box flexDirection="row" gap="s16">
        <Box width="full">
          <InlineFields>
            <Controller
              name={`taxes.${index}.percentage`}
              control={control}
              render={({ field, fieldState }) => (
                <NumberField
                  mandatory={isFieldMandatory}
                  error={castError(fieldState.error)}
                  label={i18n.t('expenses_v2.forms.fields.taxes.label_tax_rate')}
                  indicator="%"
                  max={100}
                  maxDecimals={2}
                  placeholder="0"
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value)
                    const newAmount = calculateAmount(tax.base_amount, value)
                    setValue(`taxes.${index}.amount`, newAmount, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }}
                  readonly={isFieldReadOnly}
                />
              )}
            />
            <Controller
              name={`taxes.${index}.base_amount`}
              control={control}
              render={({ field, fieldState }) => (
                <NumberField
                  label={i18n.t('expenses_v2.forms.fields.taxes.label_base_amount')}
                  mandatory={isFieldMandatory}
                  error={castError(fieldState.error)}
                  indicator={currency}
                  placeholder="0"
                  value={field.value}
                  maxDecimals={2}
                  onChange={(value) => {
                    field.onChange(value)
                    const newAmount = calculateAmount(value, tax.percentage)
                    setValue(`taxes.${index}.amount`, newAmount, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }}
                  readonly={isFieldReadOnly}
                />
              )}
            />
            <Controller
              name={`taxes.${index}.amount`}
              control={control}
              render={({ field, fieldState }) => (
                <NumberField
                  mandatory={isFieldMandatory}
                  error={castError(fieldState.error)}
                  value={field.value}
                  label={i18n.t('expenses_v2.forms.fields.taxes.label_amount')}
                  maxDecimals={2}
                  indicator={currency}
                  placeholder="0"
                  onChange={(value) => {
                    field.onChange(value)
                  }}
                  readonly={isFieldReadOnly}
                />
              )}
            />
          </InlineFields>
        </Box>
        {!hideDelete && (
          <Box flexDirection="row" gap="s16">
            <Box paddingTop="s32" alignItems="flexStart">
              <Button
                type="secondary"
                onClick={() => onDelete(index)}
                iconName="delete"
                aria-label={i18n.t('common.delete')}
              />
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}

export default TaxFields
